import type { AxiosInstance } from 'axios';
import type { ApiRentalListingList, ApiRentalListingListParams } from 'src/types/rental';

import { useQuery, queryOptions, keepPreviousData } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { READ_TIMEOUT_MS } from 'src/utils/requestError';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { askUnlessServerAnswered } from './serverRendered';

/**
 * One page of the rent catalog as a query — the browser's hook below and the server's render of
 * the page ask it alike (`src/server/catalog.server.ts`).
 */
export const rentalListingsQuery = (
  params?: ApiRentalListingListParams,
  http: Pick<AxiosInstance, 'get'> = axios
) =>
  queryOptions({
    queryKey: QueryKeys.rentalListings(params),
    queryFn: async (): Promise<ApiRentalListingList> => {
      const response = await http.get<ApiRentalListingList>('/buyer/rentals', {
        params,
        timeout: READ_TIMEOUT_MS,
      });
      return response.data;
    },
    staleTime: LIVE_STALE_TIME,
  });

/**
 * Available rental listings to browse and book. Public endpoint (no auth). `GET /buyer/rentals`
 * pages by `limit` and `offset` exactly like the sale feed — its answer just carries no `limit` or
 * `offset` of its own, only `{ items, total }` — and without them it answers the whole active set.
 * `params` narrow it (a dealer's `sellerId`, the catalog's facets, the page) and are part of the key,
 * so each dealer and each page is cached apart.
 *
 * While the next page is on its way the previous one stays on screen (`keepPreviousData`), the way
 * `useListingsPage` does for sale: the grid does not drop to a spinner and back on every page link.
 * `enabled: false` sends nothing — an address naming a make the catalog does not know.
 */
export function useListRentalListings(params?: ApiRentalListingListParams, options?: { enabled?: boolean }) {
  return useQuery({
    ...rentalListingsQuery(params),
    refetchOnMount: askUnlessServerAnswered,
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}
